import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isAuthDisabled, mockDevUser } from '../config/authConfig';

// Detect if running in cloud environment
const isCloudEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('elb.amazonaws.com') || 
         hostname.includes('amazonaws.com') ||
         hostname.includes('cloudfront.net') ||
         (!hostname.includes('localhost') && !hostname.includes('127.0.0.1'));
};

// In cloud: use same origin with /api prefix (ALB routes to backend)
// In local: use localhost:8000 directly
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (isCloudEnvironment()) {
    // Use relative URL - ALB will route /api/* to backend
    return '';
  }
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Interceptor de autenticación para Azure AD
// ============================================================================

// Variable para almacenar la función de obtención de token
let getAccessTokenFn: (() => Promise<string | null>) | null = null;

/** Current user email - injected from AuthContext.
 * Pre-populated with the mock dev user email when auth is disabled so the
 * X-User-Email header is present even on the very first API calls (before
 * any React useEffect has had a chance to run). */
let currentUserEmail: string = isAuthDisabled() ? mockDevUser.email.toLowerCase() : '';

/**
 * Set the current user email (call this from AuthProvider after user is loaded).
 * This email is sent as the X-User-Email header on every request so the backend
 * can resolve permissions without a full JWT validation.
 */
export const setCurrentUserEmail = (email: string) => {
  currentUserEmail = email.toLowerCase().trim();
};

/**
 * Configura el interceptor de autenticación con la función de obtención de token
 * Esta función debe ser llamada desde el AuthProvider
 */
export const configureAuthInterceptor = (getAccessToken: () => Promise<string | null>) => {
  getAccessTokenFn = getAccessToken;
};

/**
 * Interceptor de request para agregar el token de autenticación
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add user email header for backend RBAC
    if (currentUserEmail) {
      config.headers['X-User-Email'] = currentUserEmail;
    }
    // Si hay una función de token configurada, obtener y agregar el token
    if (getAccessTokenFn) {
      try {
        const token = await getAccessTokenFn();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('[API] Error obtaining access token:', error);
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor de response para manejar errores de autenticación
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Si recibimos un 401 (Unauthorized), podría significar que el token expiró
    if (error.response?.status === 401) {
      console.warn('[API] Unauthorized request - token may be expired');
      // Aquí podrías disparar un evento para forzar re-login
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export interface GenerateBacklogRequest {
  context_path?: string;
  additional_instructions?: string;
}

export interface GenerateBacklogWithContextRequest {
  repository_name?: string;
  feature_description: string;
  additional_instructions?: string;
  max_context_tokens?: number;
  include_standard_tasks?: boolean;
}

export interface BacklogResponse {
  success: boolean;
  backlog?: string;
  error?: string;
}

export interface HealthResponse {
  status: string;
  message: string;
  agent_initialized: boolean;
}

export interface PathValidationResponse {
  valid: boolean;
  exists: boolean;
  is_directory: boolean;
  files_count: number;
  path: string;
}

// File Upload Types (for cloud deployment)
export interface UploadContextResponse {
  success: boolean;
  upload_id: string;
  path: string;
  repository_name: string;
  files_count: number;
  errors: string[];
  message: string;
}

export interface UploadInfoResponse {
  upload_id: string;
  path: string;
  repository_name: string;
  files_count: number;
  valid: boolean;
}

// Context Store Types
export interface ContextStoreStatus {
  status: string;
  documents: number;
  chunks: number;
  repositories: string[];
  database: string;
  error?: string;
}

export interface IngestRepositoryRequest {
  directory_path: string;
  repository_name?: string;
  exclude_patterns?: string[];
  include_extensions?: string[];
  max_files?: number;
}

export interface IngestRepositoryResponse {
  success: boolean;
  repository: string;
  documents_processed: number;
  chunks_created: number;
  errors: number;
  duration_seconds: number;
  message: string;
  error?: string;
}

export interface SearchContextRequest {
  query: string;
  max_results?: number;
  file_types?: string[];
  service_name?: string;
}

export interface SearchResult {
  file_path: string;
  service: string | null;
  chunk_type: string;
  relevance_score: number;
  content_preview: string;
}

export interface SearchContextResponse {
  success: boolean;
  query: string;
  total_results: number;
  results: SearchResult[];
  error?: string;
}

export interface StandardTask {
  title: string;
  description: string;
  type: string;
  estimated_hours: number;
}

export interface StandardTasksResponse {
  success: boolean;
  standard_tasks: StandardTask[];
  count: number;
  message: string;
}

export interface AzureDevOpsConfig {
  id: string;
  name: string;
  server_url: string;
  organization: string;
  project: string;
  token: string;
  area_path?: string;
  iteration_path?: string;
}

export interface AzureDevOpsConfigCreate {
  name: string;
  server_url: string;
  organization: string;
  project: string;
  token: string;
  area_path?: string;
  iteration_path?: string;
}

export interface AzureDevOpsConfigsResponse {
  configs: AzureDevOpsConfig[];
}

export interface PublishBacklogResponse {
  success: boolean;
  epics_created?: number;
  stories_created?: number;
  tasks_created?: number;
  links_created?: number;
  items?: Array<{
    type: string;
    id: number;
    title: string;
    url: string;
    parent_epic?: string;
    tasks?: Array<{
      id: number;
      title: string;
      url: string;
    }>;
  }>;
  errors?: string[];
  error?: string;
}

export interface PublishAzureStoryPayload {
  id?: string;
  title: string;
  user_story?: string;
  rol?: string;
  criterios_funcionales?: string[];
  criterios_tecnicos?: string[];
  figma_links?: string[];
  insumos?: string[];
  story_points?: number;
  priority?: string;
  priority_sequence?: number | null;
  /** Epic name (text) or existing ADO epic ID (numeric string) */
  epic?: string;
  status?: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  project?: string;
}

/**
 * Check API health status
 */
export const checkHealth = async (): Promise<HealthResponse> => {
  const response = await api.get<HealthResponse>('/health');
  return response.data;
};

/**
 * Generate backlog
 */
export const generateBacklog = async (
  request: GenerateBacklogRequest
): Promise<BacklogResponse> => {
  const response = await api.post<BacklogResponse>('/api/generate-backlog', request);
  return response.data;
};

/**
 * Validate directory path
 */
export const validatePath = async (path: string): Promise<PathValidationResponse> => {
  const response = await api.post<PathValidationResponse>('/api/validate-path', { path });
  return response.data;
};

/**
 * Upload files to create a context directory on the server
 * This is used when the backend is running remotely (cloud/ECS)
 */
export const uploadContextFiles = async (
  files: FileList | File[],
  repositoryName?: string,
  onProgress?: (percent: number) => void
): Promise<UploadContextResponse> => {
  const formData = new FormData();
  
  // Add all files to FormData
  const fileArray = Array.from(files);
  
  console.log(`[API] Uploading ${fileArray.length} files`);
  
  for (const file of fileArray) {
    // Use webkitRelativePath if available to preserve directory structure
    let relativePath = (file as any).webkitRelativePath || file.name;
    
    // Normalize path for Windows (replace backslashes with forward slashes)
    relativePath = relativePath.replace(/\\/g, '/');
    
    // Remove any leading slashes or drive letters (C:/, D:/, etc.)
    relativePath = relativePath.replace(/^[a-zA-Z]:\//, '').replace(/^\/+/, '');
    
    console.log(`[API] Adding file: ${file.name} as ${relativePath} (${file.size} bytes)`);
    formData.append('files', file, relativePath);
  }
  
  if (repositoryName) {
    formData.append('repository_name', repositoryName);
  }
  
  try {
    // Use the configured api instance to leverage auth interceptors
    const response = await api.post<UploadContextResponse>(
      '/api/upload-context',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes timeout for large uploads
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`[API] Upload progress: ${percent}%`);
            onProgress(percent);
          }
        },
      }
    );
    
    console.log(`[API] Upload successful:`, response.data);
    return response.data;
    
  } catch (error: any) {
    console.error('[API] Upload failed:', error);
    
    // Enhance error message for better debugging
    if (error.code === 'ERR_NETWORK' || error.code === 'ERR_FILE_NOT_FOUND') {
      throw new Error(
        'Error de conexión con el servidor. Verifica tu conexión a internet y que el servidor esté funcionando.'
      );
    } else if (error.code === 'ECONNABORTED') {
      throw new Error(
        'Tiempo de espera agotado. Los archivos pueden ser demasiado grandes. Intenta con menos archivos.'
      );
    } else if (error.response) {
      // Server responded with error
      const serverMsg = error.response.data?.detail || error.response.data?.message || 'Error del servidor';
      throw new Error(serverMsg);
    } else {
      throw error;
    }
  }
};

/**
 * Get information about an uploaded context
 */
export const getUploadInfo = async (uploadId: string): Promise<UploadInfoResponse> => {
  const response = await api.get<UploadInfoResponse>(`/api/upload-context/${uploadId}`);
  return response.data;
};

/**
 * Delete an uploaded context
 */
export const deleteUpload = async (uploadId: string): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/upload-context/${uploadId}`);
  return response.data;
};

/**
 * Generate backlog with streaming (for future implementation)
 */
export const generateBacklogStream = async (
  request: GenerateBacklogRequest,
  onChunk: (text: string) => void,
  onError: (error: string) => void,
  onComplete: () => void
): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/generate-backlog-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No reader available');
    }

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        onComplete();
        break;
      }

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.text) {
              onChunk(data.text);
            } else if (data.error) {
              onError(data.error);
            } else if (data.done) {
              onComplete();
            }
          } catch (e) {
            // Ignore parsing errors for incomplete chunks
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Unknown error');
  }
};

/**
 * Azure DevOps API Functions
 */

/**
 * List all Azure DevOps configurations
 */
export const listAzureDevOpsConfigs = async (): Promise<AzureDevOpsConfigsResponse> => {
  const response = await api.get<AzureDevOpsConfigsResponse>('/api/azure-devops/configs');
  return response.data;
};

/**
 * Create a new Azure DevOps configuration
 */
export const createAzureDevOpsConfig = async (
  config: AzureDevOpsConfigCreate
): Promise<AzureDevOpsConfig> => {
  const response = await api.post<AzureDevOpsConfig>('/api/azure-devops/configs', config);
  return response.data;
};

/**
 * Get a specific Azure DevOps configuration
 */
export const getAzureDevOpsConfig = async (configId: string): Promise<AzureDevOpsConfig> => {
  const response = await api.get<AzureDevOpsConfig>(`/api/azure-devops/configs/${configId}`);
  return response.data;
};

/**
 * Delete an Azure DevOps configuration
 */
export const deleteAzureDevOpsConfig = async (configId: string): Promise<void> => {
  await api.delete(`/api/azure-devops/configs/${configId}`);
};

/**
 * Test connection to Azure DevOps
 */
export const testAzureDevOpsConnection = async (
  configId: string
): Promise<TestConnectionResponse> => {
  const response = await api.post<TestConnectionResponse>('/api/azure-devops/test-connection', {
    config_id: configId
  });
  return response.data;
};

/**
 * Publish backlog to Azure DevOps Boards.
 * When structured stories are supplied they take precedence over the raw
 * markdown text, enabling correct epic grouping and existing-epic linking.
 */
export const publishBacklogToAzure = async (
  configId: string,
  backlogText: string,
  stories?: PublishAzureStoryPayload[]
): Promise<PublishBacklogResponse> => {
  const payload: Record<string, unknown> = {
    config_id: configId,
    backlog_text: backlogText,
  };
  if (stories && stories.length > 0) {
    payload.stories = stories;
  }
  const response = await api.post<PublishBacklogResponse>('/api/azure-devops/publish', payload);
  return response.data;
};

// ============================================================================
// CONTEXT STORE API
// ============================================================================

/**
 * Get Context Store status
 */
export const getContextStoreStatus = async (): Promise<ContextStoreStatus> => {
  const response = await api.get<ContextStoreStatus>('/api/context-store/status');
  return response.data;
};

/**
 * Ingest a repository into the Context Store
 */
export const ingestRepository = async (
  request: IngestRepositoryRequest
): Promise<IngestRepositoryResponse> => {
  const response = await api.post<IngestRepositoryResponse>('/api/context-store/ingest', request);
  return response.data;
};

/**
 * Search the Context Store
 */
export const searchContext = async (
  request: SearchContextRequest
): Promise<SearchContextResponse> => {
  const response = await api.post<SearchContextResponse>('/api/context-store/search', request);
  return response.data;
};

/**
 * Get standard tasks
 */
export const getStandardTasks = async (): Promise<StandardTasksResponse> => {
  const response = await api.get<StandardTasksResponse>('/api/context-store/standard-tasks');
  return response.data;
};

/**
 * Generate backlog with Deep Analysis (análisis exhaustivo)
 * Nuevo endpoint optimizado que NO requiere MongoDB
 */
export const generateBacklogWithContext = async (
  request: GenerateBacklogWithContextRequest
): Promise<BacklogResponse> => {
  // Mapear el request antiguo al nuevo formato
  const newRequest = {
    directory_path: request.feature_description || '',
    feature_description: request.additional_instructions || request.feature_description,
    additional_instructions: request.additional_instructions,
    max_files: 30,
    analysis_depth: 'deep',
    include_standard_tasks: request.include_standard_tasks ?? true
  };
  
  const response = await api.post<BacklogResponse>('/api/generate-backlog-deep-analysis', newRequest);
  return response.data;
};

/**
 * Clear Context Store
 */
export const clearContextStore = async (
  repositoryName?: string
): Promise<{ success: boolean; documents_deleted: number; chunks_deleted: number }> => {
  const response = await api.delete('/api/context-store/clear', {
    params: repositoryName ? { repository_name: repositoryName } : {}
  });
  return response.data;
};

// ============================================================================
// Excel Export
// ============================================================================

export interface ExcelStoryRequest {
  id: string;
  title: string;
  user_story: string;
  rol: string;
  criterios_funcionales: string[];
  criterios_tecnicos: string[];
  figma_links: string[];
  insumos: string[];
  story_points: number;
  priority: string;
  epic?: string;
  status: string;
  tasks: Array<{
    title: string;
    description?: string;
    type: string;
    estimated_hours: number;
  }>;
}

export interface ExportExcelRequest {
  stories: ExcelStoryRequest[];
  executive_summary?: string;
}

/**
 * Export backlog to Excel file
 * Returns a Blob that can be downloaded
 */
export const exportToExcel = async (request: ExportExcelRequest): Promise<Blob> => {
  const response = await api.post('/api/export-excel', request, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Helper function to download the Excel file
 */
export const downloadExcel = async (request: ExportExcelRequest): Promise<void> => {
  try {
    const blob = await exportToExcel(request);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backlog_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading Excel:', error);
    throw error;
  }
};

export default api;

// ============================================================================
// TEAMS & PROJECTS API
// ============================================================================

export interface Team {
  id: string;
  name: string;
  description: string;
  members: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SharedProject {
  id: string;
  name: string;
  description: string;
  owner: string;
  team_id: string | null;
  backlog: string | null;
  context_path: string;
  instructions: string;
  created_at: string;
  updated_at: string;
}

export interface TeamListResponse {
  teams: Team[];
  is_superadmin: boolean;
}

export interface ProjectListResponse {
  projects: SharedProject[];
  is_superadmin: boolean;
}

// ---- Teams -----------------------------------------------------------------

export const listTeams = async (): Promise<TeamListResponse> => {
  const res = await api.get<TeamListResponse>('/api/teams');
  return res.data;
};

export const createTeam = async (
  name: string, description: string, members?: string[]
): Promise<Team> => {
  const res = await api.post<Team>('/api/teams', { name, description, members });
  return res.data;
};

export const updateTeam = async (
  teamId: string, updates: Partial<Pick<Team, 'name' | 'description'>>
): Promise<{ success: boolean }> => {
  const res = await api.put(`/api/teams/${teamId}`, updates);
  return res.data;
};

export const deleteTeam = async (teamId: string): Promise<{ success: boolean }> => {
  const res = await api.delete(`/api/teams/${teamId}`);
  return res.data;
};

export const addTeamMember = async (teamId: string, email: string): Promise<{ success: boolean }> => {
  const res = await api.post(`/api/teams/${teamId}/members`, { email });
  return res.data;
};

export const removeTeamMember = async (
  teamId: string, memberEmail: string
): Promise<{ success: boolean }> => {
  const res = await api.delete(`/api/teams/${teamId}/members/${encodeURIComponent(memberEmail)}`);
  return res.data;
};

// ---- Projects --------------------------------------------------------------

export const listSharedProjects = async (): Promise<ProjectListResponse> => {
  const res = await api.get<ProjectListResponse>('/api/projects');
  return res.data;
};

export const createSharedProject = async (payload: {
  name: string;
  description?: string;
  team_id?: string;
  context_path?: string;
  instructions?: string;
}): Promise<SharedProject> => {
  const res = await api.post<SharedProject>('/api/projects', payload);
  return res.data;
};

export const getSharedProject = async (projectId: string): Promise<SharedProject> => {
  const res = await api.get<SharedProject>(`/api/projects/${projectId}`);
  return res.data;
};

export const updateSharedProject = async (
  projectId: string,
  updates: Partial<Pick<SharedProject, 'name' | 'description' | 'team_id' | 'context_path' | 'instructions' | 'backlog'>>
): Promise<SharedProject> => {
  const res = await api.put<SharedProject>(`/api/projects/${projectId}`, updates);
  return res.data;
};

export const deleteSharedProject = async (projectId: string): Promise<{ success: boolean }> => {
  const res = await api.delete(`/api/projects/${projectId}`);
  return res.data;
};

// ---- Role info -------------------------------------------------------------

export const getMyRole = async (): Promise<{ email: string; is_superadmin: boolean; role: string }> => {
  const res = await api.get('/api/me/role');
  return res.data;
};
